import { ReactNode } from "react";

import { Notice } from "@/components/ui";
import type { ComponentSource, PendingImport } from "@/types/api";

/**
 * Says where a catalog component comes from, and — separately — whether an
 * import has a change waiting on a human. Two independent concerns sharing
 * one banner because they share one cause (§5.4 / the accept-route brief):
 *
 * - ``source`` present → the build owns ``description``/``settings``/
 *   ``runtime``/``depends_on``/``image_uri``/``image_tag``; the page locks
 *   those fields (``JsonPayloadEditor readOnly``) and this notice says
 *   where to edit them instead — a correction typed here would be
 *   overwritten, silently, at the next build.
 * - ``pendingImport`` present → the last import proposed ``inclusion``
 *   and/or ``mandatory`` without applying them (an import never writes
 *   either — see ``PendingImport`` in models.py). A component declared
 *   without ``mandatory`` reaches no gateway until this is accepted: that
 *   silence is exactly what this banner exists to prevent.
 *
 * Each half renders independently — a brick can carry ``source`` without a
 * pending change (the common case after acceptance) or, in principle, the
 * reverse. Renders nothing at all when both are absent.
 */
export interface ImportedFieldsNoticeProps {
  source: ComponentSource | null;
  pendingImport: PendingImport | null;
  /**
   * Rendered inside the pending-change block, e.g. an "Accept" button wired
   * to the accept mutation. Omitted on pages that only need to surface the
   * pending change, not act on it.
   */
  pendingAction?: ReactNode;
}

function proposedChanges(pending: PendingImport): string[] {
  const changes: string[] = [];
  // `!= null` (not `!== null`) covers `undefined` too — a backend field that
  // ever arrives `undefined` rather than `null` would slip past a strict
  // `!== null` guard and render "Inclusion → Optional" for a proposal that
  // says nothing about inclusion. This is the exact sentence a human reads
  // to decide whether to enrol or unenrol the whole fleet, and there is no
  // front-end test runner in this repo to catch the regression another way.
  if (pending.inclusion != null) {
    const label = pending.inclusion === "always" ? "Always included" : "Optional";
    changes.push(`Inclusion → ${label}`);
  }
  if (pending.mandatory != null) {
    changes.push(`Mandatory → ${pending.mandatory ? "true" : "false"}`);
  }
  return changes;
}

export function ImportedFieldsNotice({
  source,
  pendingImport,
  pendingAction,
}: ImportedFieldsNoticeProps) {
  if (!source && !pendingImport) return null;

  return (
    <div className="space-y-2 mb-4">
      {source && (
        <Notice tone="info">
          Imported from <span className="font-mono">{source.repo}</span> /{" "}
          <span className="font-mono">{source.service}</span> at{" "}
          <span className="font-mono">{source.buildId}</span>. Edit the
          compose service to change it.
        </Notice>
      )}
      {pendingImport && (
        <Notice
          tone="warning"
          title="Pending change from the last import — not applied yet."
        >
          <p className="mt-1">
            {proposedChanges(pendingImport).join(" · ")}. Until this is
            accepted, the component stays as configured today and reaches no
            gateway through this change.
          </p>
          {pendingAction && <div className="mt-2">{pendingAction}</div>}
        </Notice>
      )}
    </div>
  );
}
